import React, {useEffect, useState} from 'react';
import {Table, Select, Switch, Button, Avatar, Tag, message, Spin} from 'antd';
import {UserOutlined} from '@ant-design/icons';
import {useAuth} from '../context/auth-context';
import {ROLE_RANK} from '../utils/role-rank';
import './admin-users.scss';

const ROLES = Object.keys(ROLE_RANK);

// /admin/users — Administrator-only inline editor for role + workgroups.
// Defensive client-side gate (the API also enforces Administrator role).
// Autosave deferred for v1: edits stage on the row, "Save" PATCHes the
// diff. Audit logging happens server-side; this page just refreshes.

function AdminUsers() {
    const {user} = useAuth();
    const [users, setUsers] = useState(null);
    const [edits, setEdits] = useState({}); // {userId: {role?, workgroups?}}
    const [saving, setSaving] = useState({});
    const [error, setError] = useState(null);

    useEffect(() => {
        if (user.role !== 'Administrator') return;
        fetch('/api/users')
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then((d) => setUsers(d.users))
            .catch((err) => setError(err.message));
    }, [user.role]);

    if (user.role !== 'Administrator') {
        return (
            <div className="portal-admin-users__forbidden">
                403 — Administrator role required.
            </div>
        );
    }

    if (error) return <div className="portal-admin-users__error">Error loading users: {error}</div>;
    if (users === null) return <div className="portal-admin-users__loading"><Spin /></div>;

    function stage(id, patch) {
        setEdits((prev) => ({...prev, [id]: {...(prev[id] || {}), ...patch}}));
    }

    function effective(row) {
        const e = edits[row.id] || {};
        return {
            role: e.role !== undefined ? e.role : row.role,
            workgroups: e.workgroups !== undefined ? e.workgroups : row.workgroups,
        };
    }

    function isDirty(row) {
        const e = edits[row.id];
        if (!e) return false;
        if (e.role !== undefined && e.role !== row.role) return true;
        if (e.workgroups !== undefined && JSON.stringify(e.workgroups) !== JSON.stringify(row.workgroups)) return true;
        return false;
    }

    async function save(row) {
        const e = edits[row.id] || {};
        const patch = {};
        if (e.role !== undefined && e.role !== row.role) patch.role = e.role;
        if (e.workgroups !== undefined && JSON.stringify(e.workgroups) !== JSON.stringify(row.workgroups)) {
            patch.workgroups = e.workgroups;
        }
        if (Object.keys(patch).length === 0) return;
        setSaving((s) => ({...s, [row.id]: true}));
        try {
            const r = await fetch(`/api/users/${row.id}`, {
                method: 'PATCH',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(patch),
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const d = await r.json();
            setUsers((prev) => prev.map((u) => (u.id === row.id ? d.user : u)));
            setEdits((prev) => {
                const next = {...prev};
                delete next[row.id];
                return next;
            });
            message.success(`Saved ${d.user.email}`);
        } catch (err) {
            message.error(`Save failed: ${err.message}`);
        } finally {
            setSaving((s) => {
                const next = {...s};
                delete next[row.id];
                return next;
            });
        }
    }

    const columns = [
        {
            title: 'User',
            key: 'user',
            render: (_, row) => (
                <div className="portal-admin-users__person">
                    <Avatar size={28} src={row.avatar_url || undefined} icon={!row.avatar_url ? <UserOutlined /> : undefined} />
                    <div>
                        <div className="portal-admin-users__name">{row.display_name || '(no name)'}{row.id === user.id && <Tag color="blue" style={{marginLeft: 6}}>you</Tag>}</div>
                        <div className="portal-admin-users__email">{row.email}</div>
                    </div>
                </div>
            ),
        },
        {
            title: 'Role',
            key: 'role',
            width: 180,
            render: (_, row) => {
                const eff = effective(row);
                return (
                    <Select
                        value={eff.role}
                        options={ROLES.map((r) => ({label: r, value: r}))}
                        onChange={(v) => stage(row.id, {role: v})}
                        style={{width: '100%'}}
                    />
                );
            },
        },
        {
            title: 'Workgroups',
            key: 'workgroups',
            render: (_, row) => {
                const eff = effective(row);
                const all = eff.workgroups === null;
                return (
                    <div className="portal-admin-users__wg">
                        <div className="portal-admin-users__wg-toggle">
                            <Switch checked={all} onChange={(checked) => stage(row.id, {workgroups: checked ? null : []})} />
                            <span>All workgroups</span>
                        </div>
                        <Select
                            mode="tags"
                            value={all ? [] : eff.workgroups || []}
                            onChange={(v) => stage(row.id, {workgroups: v})}
                            placeholder="add workgroup slugs…"
                            disabled={all}
                            style={{width: '100%'}}
                            tokenSeparators={[',']}
                        />
                    </div>
                );
            },
        },
        {
            title: '',
            key: 'save',
            width: 90,
            render: (_, row) => (
                <Button
                    type="primary"
                    size="small"
                    onClick={() => save(row)}
                    disabled={!isDirty(row)}
                    loading={Boolean(saving[row.id])}
                >
                    Save
                </Button>
            ),
        },
    ];

    return (
        <div className="portal-admin-users">
            <h1 className="portal-admin-users__title">Manage users</h1>
            <p className="portal-admin-users__subtitle">
                Edit role + workgroups. Changes audit-log to the server console (Day 14 routes them to the audit table).
            </p>
            <Table
                rowKey="id"
                columns={columns}
                dataSource={users}
                pagination={false}
                size="middle"
            />
        </div>
    );
}

export default AdminUsers;
